import { FC, ReactElement, useEffect } from 'react'
import styled, { useTheme } from 'styled-components'
import { useSpring } from '@react-spring/core'
import { animated } from '@react-spring/web'
import useInterpolateColors from '../../Hooks/useInterpolateColors'
import ExampleComponent from '../ExampleComponent'

interface Props {
  title?: string
  lead?: string
  keywords?: string
  date?: string
  authors?: string
}
const Header: FC<Props> = ({
  keywords,
  title,
  lead,
  date,
  authors,
}): ReactElement => {
  const theme = useTheme()
  const interpolateColors = useInterpolateColors()
  const [spring, api] = useSpring(() => ({
    pct: 0,
  }))
  const onScroll = () => {
    const scroll = window.scrollY
    api.start({
      pct: Math.min((scroll / window.innerHeight) * 4, 1),
    })
  }

  useEffect(() => {
    window.addEventListener('scroll', onScroll)

    return () => {
      window.removeEventListener('scroll', onScroll)
    }
  }, [api])

  const containerColor = spring.pct.to((pct) =>
    interpolateColors(theme.backgroundColor, '#222426', pct)
  )

  const textColor = spring.pct.to((pct) =>
    interpolateColors(theme.textColor, theme.textColorInverted, pct)
  )

  const supColor = spring.pct.to((pct) =>
    interpolateColors(theme.tenantColor, theme.textColorInverted, pct)
  )

  return (
    <Container style={{ backgroundColor: containerColor }}>
      <TextContainer
        style={{ color: textColor, zIndex: 1, position: 'relative' }}
      >
        <TextWidth>
          <>
            {title && (
              <TitleContainer>
                <Sup
                  style={{ color: supColor, zIndex: 1, position: 'relative' }}
                >
                  {keywords}
                </Sup>
                <Title>{title}</Title>
              </TitleContainer>
            )}
            {lead && <Text>{lead}</Text>}
            {authors && <Authors>{authors}</Authors>}
            {date && <Date>{date}</Date>}
          </>
        </TextWidth>
      </TextContainer>
      <Sun
        style={{
          filter: spring.pct.to((pct) => `blur(${(1 - pct) * 100}px)`),
          transform: spring.pct.to(
            (pct) =>
              `translate(-50%, ${pct * 100}${
                window.innerWidth > window.innerHeight ? 'vmin' : 'vmax'
              })`
          ),
        }}
      />
      <ExampleComponent />
    </Container>
  )
}

const Sun = styled(animated.div)`
  border-radius: 50%;
  background: ${({ theme }) => theme.colorMode === 'dark' ? '#222426' : '#ffffff'};
  position: fixed;
  top: 0;
  left: 50%;
  filter: blur(50px);
  z-index: 0;
  width: 85vmin;
  height: 85vmin;

  // @media (orientation: portrait) {
  //   width: 85vw;
  //   height: 85vw;
  // }
`

const TextContainer = styled(animated.div)``

const Dimmer = styled(animated.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`

const TextWidth = styled.div`
  max-width: 750px;
  margin: 0 auto;
  ${({ theme }) => theme.breakpoints.down('md')} {
    padding: 0 1.5rem;
  }
`

const Container = styled(animated.div)`
  position: relative;
  margin: auto;
  padding-top: 20px;
`

const TitleContainer = styled.h2`
  margin-top: 0;
  line-height: 1.15;
  text-align: center;
  margin-bottom: 1.2rem;
  font-size: 2.85em;

  ${({ theme }) => theme.breakpoints.down('md')} {
    margin-bottom: 1.2rem;
    font-size: 2em;
  }
`

const Title = styled.span`
  font-family: var(--font-serif);
  font-weight: 600;
  display: block;

  -webkit-font-smoothing: antialiased;
`

const Text = styled.h3`
  font-family: var(--font-serif);
  font-weight: 400;
  text-align: center;

  font-size: 1.4em;
  line-height: 1.4;

  ${({ theme }) => theme.breakpoints.down('md')} {
    font-size: 1em;
  }
`

const Sup = styled(animated.span)`
  display: inline-block;
  min-width: 85%;
  margin-bottom: 1.5rem;
  font-size: 1.5rem;
  font-family: var(--font-sans-serif);
  line-height: 2rem;

  // &:before {
  //   display: inline-block;
  //   margin-right: 0.6rem;
  //   padding: 0.2rem 0.6rem;
  //   color: white;
  //   font-weight: 700;
  //   font-size: 1.2rem;
  //   font-family: var(--font-sans-serif);
  //   line-height: 1.5rem;
  //   letter-spacing: 0.025rem;
  //   white-space: nowrap;
  //   background-color: ${({ theme }) => theme.brandColor};
  //   content: 'Abo';
  // }
`

const Authors = styled.div`
  font-family: var(--font-sans-serif);
  font-size: 1.4rem;
  line-height: 1.2;
  margin-bottom: 0.5rem;
  font-weight: bold;
  margin-top: 2rem;
  text-align: center;
`

const Date = styled.div`
  font-size: 1.2rem;
  font-family: var(--font-sans-serif);
  opacity: 0.65;
  line-height: 1.2;
  text-align: center;
`

export default Header
